export const paths = {
    // Phase 4 slice F2b — the OAuth consent screen. Under /app because
    // approving a grant needs a signed-in user; the signed request the
    // authorize endpoint issues travels in `?request=`.
    authorize: () => '/app/authorize',
    activateAccount: (confirmHash) => confirmHash ? `/activate-account/${confirmHash}` : '/activate-account',
    workspace: () => '/app/workspace',
    forgot: () => '/forgot',
    forgotSuccess: () => '/forgot/success',
    // Phase 3 slice S — landing page for item-share invites. The hash
    // is the ItemShareInvite.inviteHash carried in the recipient's
    // email; the page decides which of the three branches to render
    // (unauth signup CTAs / silent claim / add-and-verify) based on
    // the caller's auth state and verifiedEmails set.
    shareInvite: (inviteHash) => inviteHash ? `/invite/${inviteHash}` : '/invite',
    loggedInRoot: () => '/app',
    /**
     * Phase 4 slice M — a link straight to one item.
     *
     * A query parameter on the workspace rather than a route of its own,
     * because the destination IS the workspace: the item is opened in
     * its box, in place, exactly as clicking it from the sidebar would.
     * A dedicated route would have to reproduce that, and would leave
     * the user somewhere the app has no other way of reaching.
     *
     * Used by the confirmations external surfaces send back — Slack and
     * MCP — where the thing captured is otherwise out of sight.
     */
    workspaceItem: (itemShareId) => itemShareId
        ? `/app/workspace?item=${encodeURIComponent(itemShareId)}`
        : '/app/workspace',
    login: () => '/login',
    logout: () => '/app/logout',
    notLoggedInRoot: () => '/',
    organiser: () => '/app/organiser',
    prioritized: () => '/app/prioritized',
    backlog: () => '/app/backlog',
    passwordReset: ({ passwordResetHash = null, } = {}) => passwordResetHash
        ? `/password-reset/${passwordResetHash}`
        : '/password-reset',
    passwordResetSuccess: () => '/password-reset-success',
    resendActivationLink: () => '/resend-activation-link',
    resendActivationLinkSuccess: () => '/resend-activation-link-success',
    shares: () => '/app/shares',
    signup: (organisationInviteHash) => organisationInviteHash
        ? `/signup/${organisationInviteHash}`
        : '/signup',
    signupSuccess: () => '/signup/success',
    verifyEmail: (token) => token ? `/app/verify-email/${token}` : '/app/verify-email',
};
/**
 * Validate a post-login return path.
 *
 * Login accepts `?next=` so a link into the app survives the detour
 * through the login form — the OAuth consent screen is the reason it
 * exists, since bouncing a user to the workspace mid-flow leaves the
 * client waiting for a redirect that never comes.
 *
 * That parameter is attacker-supplied, and a login page that will
 * redirect anywhere is a phishing primitive: send someone a real tidee
 * login link, let them authenticate, land them on a convincing copy.
 * So this is an allow-list, not a sanitiser.
 *
 *   - Must start with a single "/" — no absolute URLs, and no
 *     protocol-relative "//evil.com", which a naive startsWith('/')
 *     check would wave through.
 *   - No backslashes, which some browsers normalise to "/" and which
 *     would smuggle "/\evil.com" past the check above.
 *   - Must be under /app, the only place a return is meaningful.
 *
 * Returns the path, or null. Callers send the user to their default
 * destination on null rather than trying to repair the value.
 */
export const safeNextPath = (value) => {
    if (typeof value !== 'string' || !value)
        return null;
    if (value.includes('\\'))
        return null;
    if (!value.startsWith('/') || value.startsWith('//'))
        return null;
    const pathOnly = value.split('?')[0].split('#')[0];
    if (pathOnly !== '/app' && !pathOnly.startsWith('/app/'))
        return null;
    return value;
};
//# sourceMappingURL=index.js.map